import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Heart, ShoppingCart, Star, Loader2, Store, Wrench, Search, X } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Breadcrumbs from '../components/Breadcrumbs';
import { useFavorites } from '../context/FavoritesContext';
import { useCart } from '../context/CartContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CategoryPage = () => {
  const { country, categorySlug } = useParams();
  const navigate = useNavigate();
  const { isFavorite, toggleFavorite } = useFavorites();
  const { addToCart } = useCart();
  const [sortBy, setSortBy] = useState('rating');
  const [products, setProducts] = useState([]);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredProducts, setFilteredProducts] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch categories first to find the right one
        const categoriesRes = await fetch(`${API_URL}/api/categories`);
        const categoriesData = await categoriesRes.json();
        
        const foundCategory = categoriesData.find(cat => cat.slug === categorySlug);
        setCategory(foundCategory);

        if (foundCategory) {
          // Fetch products for this category
          const productsRes = await fetch(`${API_URL}/api/products?category_id=${foundCategory.id}&sort_by=${sortBy}&limit=1000`);
          const productsData = await productsRes.json();
          
          // Sort products based on selected option
          let sortedProducts = [...productsData];
          if (sortBy === 'rating') {
            sortedProducts.sort((a, b) => (b.average_rating || 0) - (a.average_rating || 0));
          } else if (sortBy === 'sales') {
            sortedProducts.sort((a, b) => (b.total_sales || 0) - (a.total_sales || 0));
          } else if (sortBy === 'price_asc') {
            sortedProducts.sort((a, b) => (a.sale_price || a.regular_price) - (b.sale_price || b.regular_price));
          } else if (sortBy === 'price_desc') {
            sortedProducts.sort((a, b) => (b.sale_price || b.regular_price) - (a.sale_price || a.regular_price));
          }
          
          setProducts(sortedProducts);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [categorySlug, sortBy]);

  // Filter products based on search
  useEffect(() => {
    if (searchQuery.length < 2) {
      setFilteredProducts(products);
    } else {
      const query = searchQuery.toLowerCase();
      setFilteredProducts(products.filter(p => 
        p.name.toLowerCase().includes(query) ||
        p.sku?.toLowerCase().includes(query) ||
        p.description?.toLowerCase().includes(query)
      ));
    }
  }, [searchQuery, products]);

  if (loading) {
    return (
      <div className="min-h-screen bg-aug-cream flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-aug-blue" />
      </div>
    );
  }

  if (!category) {
    return (
      <div className="min-h-screen bg-aug-cream">
        <Header />
        <div className="pt-24 pb-16 text-center">
          <h1 className="text-2xl font-bold text-aug-gray-dark">Categoria não encontrada</h1>
        </div>
      </div>
    );
  }

  const handleProductClick = (productId) => {
    navigate(`/${country}/products/${productId}`);
  };

  const handleAddToCart = (e, product) => {
    e.stopPropagation();
    addToCart(product, 1);
  };

  const handleToggleFavorite = (e, product) => {
    e.stopPropagation();
    toggleFavorite(product);
  };

  const handlePartnerClick = (e, partnerId) => {
    e.stopPropagation();
    navigate(`/${country}/partner/${partnerId}`);
  };

  // Separar favoritos
  const favoriteProducts = filteredProducts.filter(p => isFavorite(p.id));
  const otherProducts = filteredProducts.filter(p => !isFavorite(p.id));

  return (
    <div className="min-h-screen bg-aug-cream">
      <Header />

      <div className="pt-24 pb-16">
        <div className="max-w-[1400px] mx-auto px-xl">
          {/* Breadcrumbs */}
          <Breadcrumbs
            items={[
              { label: 'Home', path: `/${country}` },
              { label: 'Produtos', path: `/${country}/products` },
              { label: category.name, path: `/${country}/products/category/${category.slug}` }
            ]}
          />

          {/* Category Header */}
          <div className="mb-8">
            <div className="flex items-start justify-between gap-6">
              <div className="flex-1">
                <h1 className="text-4xl font-bold mb-3" style={{ color: '#1B5E20' }}>
                  {category.name}
                </h1>
                <p className="text-lg text-aug-gray-medium mb-4">
                  {category.description}
                </p>
                <div className="flex items-center gap-4 text-sm text-aug-gray-medium">
                  <span className="flex items-center gap-1">
                    <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    <strong>{category.average_rating || 0}</strong> avaliação média
                  </span>
                  <span>•</span>
                  <span><strong>{filteredProducts.length}</strong> produtos</span>
                </div>
              </div>

              {/* Sort Controls */}
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-aug-gray-dark">Ordenar por:</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-4 py-2 border border-aug-gray-light rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-aug-blue"
                >
                  <option value="rating">Melhor Avaliados</option>
                  <option value="sales">Mais Vendidos</option>
                  <option value="price_asc">Menor Preço</option>
                  <option value="price_desc">Maior Preço</option>
                </select>
              </div>
            </div>

            {/* Search Bar */}
            <div className="mt-6 relative max-w-xl">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar nesta categoria..."
                className="w-full pl-12 pr-12 py-3 rounded-xl border-2 border-gray-200 focus:border-aug-blue focus:outline-none"
                data-testid="category-search-input"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>

          {/* Favorites Section */}
          {favoriteProducts.length > 0 && (
            <div className="mb-12">
              <div className="flex items-center gap-2 mb-4">
                <Heart className="w-5 h-5 fill-red-500 text-red-500" />
                <h2 className="text-2xl font-bold text-aug-gray-dark">
                  Meus Favoritos ({favoriteProducts.length})
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {favoriteProducts.map((product) => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    onProductClick={handleProductClick}
                    onAddToCart={handleAddToCart}
                    onToggleFavorite={handleToggleFavorite}
                    onPartnerClick={handlePartnerClick}
                    isFavorite={true}
                    country={country}
                  />
                ))}
              </div>
            </div>
          )}

          {/* All Products Section */}
          <div>
            {favoriteProducts.length > 0 && (
              <h2 className="text-2xl font-bold text-aug-gray-dark mb-4">
                Todos os Produtos ({otherProducts.length})
              </h2>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {otherProducts.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  onProductClick={handleProductClick}
                  onAddToCart={handleAddToCart}
                  onToggleFavorite={handleToggleFavorite}
                  onPartnerClick={handlePartnerClick}
                  isFavorite={false}
                  country={country}
                />
              ))}
            </div>
          </div>

          {/* Empty State */}
          {filteredProducts.length === 0 && (
            <div className="bg-white rounded-xl p-16 text-center">
              <h2 className="text-2xl font-bold text-aug-gray-dark mb-2">
                Nenhum produto encontrado
              </h2>
              <p className="text-aug-gray-medium">
                Esta categoria ainda não possui produtos.
              </p>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

// Product Card Component
const ProductCard = ({ product, onProductClick, onAddToCart, onToggleFavorite, onPartnerClick, isFavorite, country }) => {
  const navigate = useNavigate();
  const hasDiscount = product.sale_price && product.sale_price < product.regular_price;
  const discountPercent = hasDiscount
    ? Math.round(((product.regular_price - product.sale_price) / product.regular_price) * 100)
    : 0;

  return (
    <div
      className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer group"
      onClick={() => onProductClick(product.id)}
    >
      {/* Image */}
      <div className="relative h-52 overflow-hidden bg-gray-100">
        {product.images?.[0] ? (
          <img
            src={product.images[0]}
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400">
            <span className="text-xs">Sem imagem</span>
          </div>
        )}

        {/* Favorite Button */}
        <button
          onClick={(e) => onToggleFavorite(e, product)}
          className="absolute top-2 right-2 w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
        >
          <Heart
            className={`w-5 h-5 ${isFavorite ? 'fill-red-500 text-red-500' : 'text-gray-400'}`}
          />
        </button>

        {/* Discount Badge */}
        {hasDiscount && (
          <div className="absolute top-2 left-2 bg-red-500 text-white px-3 py-1 rounded-full text-sm font-bold">
            -{discountPercent}%
          </div>
        )}

        {/* Out of Stock */}
        {!product.in_stock && (
          <div className="absolute inset-0 bg-black bg-opacity-60 flex items-center justify-center">
            <span className="bg-white text-aug-gray-dark px-4 py-2 rounded-lg font-bold">
              Esgotado
            </span>
          </div>
        )}
      </div>

      {/* Info */}
      <div className="p-4">
        {/* Partner Badge */}
        {product.partner_name && (
          <div 
            onClick={(e) => onPartnerClick(e, product.partner_id)}
            className="flex items-center gap-1.5 mb-2 text-xs text-gray-500 hover:text-aug-blue transition-colors"
          >
            <Store className="w-3 h-3" />
            <span className="truncate">{product.partner_name}</span>
          </div>
        )}

        <h3 className="text-base font-semibold text-aug-gray-dark mb-2 line-clamp-2 min-h-[3rem]">
          {product.name}
        </h3>

        {/* Rating */}
        <div className="flex items-center gap-1 mb-2">
          {[1, 2, 3, 4, 5].map((star) => (
            <Star
              key={star}
              className={`w-4 h-4 ${
                star <= (product.average_rating || 0)
                  ? 'fill-yellow-400 text-yellow-400'
                  : 'text-gray-300'
              }`}
            />
          ))}
          <span className="text-xs text-aug-gray-medium ml-1">
            ({product.reviews_count || 0})
          </span>
        </div>

        {/* Price */}
        <div className="flex items-baseline gap-2 mb-3">
          <span className="text-2xl font-bold" style={{ color: '#1B5E20' }}>
            R$ {(product.sale_price || product.regular_price).toFixed(2)}
          </span>
          {hasDiscount && (
            <span className="text-sm text-aug-gray-medium line-through">
              R$ {product.regular_price.toFixed(2)}
            </span>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2 mb-3">
          <button
            onClick={(e) => onPartnerClick(e, product.partner_id)}
            className="flex-1 flex items-center justify-center gap-1 py-2 px-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-xs font-medium text-gray-700 transition-colors"
          >
            <Store className="w-3.5 h-3.5" />
            <span>Empresa</span>
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/${country}/services`);
            }}
            className="flex-1 flex items-center justify-center gap-1 py-2 px-2 bg-blue-50 hover:bg-blue-100 rounded-lg text-xs font-medium text-blue-700 transition-colors"
          >
            <Wrench className="w-3.5 h-3.5" />
            <span>Técnico</span>
          </button>
        </div>

        {/* Add to Cart Button */}
        <button
          onClick={(e) => onAddToCart(e, product)}
          disabled={!product.in_stock}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg font-medium text-white transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ backgroundColor: '#1B5E20' }}
        >
          <ShoppingCart className="w-4 h-4" />
          <span>{product.in_stock ? 'Adicionar ao Carrinho' : 'Esgotado'}</span>
        </button>
      </div>
    </div>
  );
};

export default CategoryPage;
